class HumbrahHomeHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.slides = [...this.querySelectorAll('[data-humbrah-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-humbrah-hero-dot]')];
    this.status = this.querySelector('[data-humbrah-hero-status]');
    this.pauseButton = this.querySelector('[data-humbrah-hero-pause]');
    this.playButton = this.querySelector('[data-humbrah-hero-play]');
    this.hero = this.closest('.hero');
    this.activeIndex = 0;
    this.isHovering = false;
    this.isFocusWithin = false;
    this.isManuallyPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayDelay = Math.max(4000, Number(this.dataset.interval) || 7000);
    this.autoplayAllowed = this.dataset.autoplay === 'true';

    this.querySelector('[data-humbrah-hero-previous]')?.addEventListener('click', () => {
      this.showSlide(this.activeIndex - 1);
    });
    this.querySelector('[data-humbrah-hero-next]')?.addEventListener('click', () => {
      this.showSlide(this.activeIndex + 1);
    });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.showSlide(Number(dot.dataset.humbrahHeroDot)));
    });
    this.pauseButton?.addEventListener('click', () => this.setPaused(true));
    this.playButton?.addEventListener('click', () => this.setPaused(false));

    this.addEventListener('mouseenter', () => {
      this.isHovering = true;
      this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      this.isHovering = false;
      this.startAutoplay();
    });
    this.addEventListener('focusin', () => {
      this.isFocusWithin = true;
      this.stopAutoplay();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.isFocusWithin = false;
        this.startAutoplay();
      }
    });

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') this.startAutoplay();
      else this.stopAutoplay();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.updatePlaybackControls();
    this.updateSlides(false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  showSlide(index) {
    if (!this.slides.length) return;
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.updateSlides(true);
    this.startAutoplay();
  }

  updateSlides(announce) {
    this.slides.forEach((slide, index) => {
      const active = index === this.activeIndex;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      const slideLink = slide.querySelector('a');
      if (slideLink) slideLink.tabIndex = active ? 0 : -1;
    });

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === this.activeIndex));
    });

    this.dataset.activeSlide = String(this.activeIndex);
    if (this.hero) {
      this.hero.dataset.activeSlide = String(this.activeIndex);
      this.hero.querySelectorAll('[data-humbrah-hero-promo]').forEach((promo) => {
        promo.hidden = Number(promo.dataset.humbrahHeroPromo) !== this.activeIndex;
      });
    }

    if (announce && this.status) {
      this.status.textContent = `Slide ${this.activeIndex + 1} of ${this.slides.length}`;
    }
  }

  setPaused(paused) {
    this.isManuallyPaused = paused;
    this.updatePlaybackControls();
    if (this.isManuallyPaused) this.stopAutoplay();
    else this.startAutoplay();
  }

  updatePlaybackControls() {
    if (this.pauseButton) this.pauseButton.hidden = this.isManuallyPaused;
    if (this.playButton) this.playButton.hidden = !this.isManuallyPaused;
  }

  startAutoplay() {
    this.stopAutoplay();
    if (
      !this.autoplayAllowed ||
      this.isManuallyPaused ||
      this.isHovering ||
      this.isFocusWithin ||
      document.visibilityState !== 'visible' ||
      this.slides.length < 2
    ) return;
    this.autoplayTimer = window.setInterval(() => {
      this.activeIndex = (this.activeIndex + 1) % this.slides.length;
      this.updateSlides(false);
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

if (!customElements.get('humbrah-home-hero-carousel')) {
  customElements.define('humbrah-home-hero-carousel', HumbrahHomeHeroCarousel);
}
